import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import type { AdminMemberDetail } from '../hooks/useAdminUsersQuery';
import {
  MemberCsError,
  useMemberProfileMutation,
  usePasswordResetCodeMutation,
  type MemberProfilePatch,
  type PasswordResetChannel,
  type PasswordResetCodeResult,
} from '../hooks/useMemberCsMutations';

const REASON_MAX = 200;
const NAME_MAX = 50;
const EMAIL_MAX = 254;

type ProfileField = 'name' | 'email' | 'mobileCountryCode' | 'mobileNumber' | 'birthDate';

/** 가린 값("010-****-5678", "홍*동")은 폼에 싣지 않는다 — 그대로 다시 보내면 원문이 덮인다. */
const editable = (v: string | null | undefined): string => (v && !v.includes('*') ? v : '');

const todayYmd = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** 바뀐 필드만 담는다(BE: null/미전송 = 유지). 빈 칸은 "바꾸지 않음" — 지우기는 없다. */
export const buildProfilePatch = (
  initial: Record<ProfileField, string>,
  current: Record<ProfileField, string>,
  reason: string,
): MemberProfilePatch | null => {
  const patch: MemberProfilePatch = { reason: reason.trim() };
  let changed = false;
  for (const key of Object.keys(current) as ProfileField[]) {
    const v = current[key].trim();
    if (v !== '' && v !== initial[key].trim()) {
      patch[key] = v;
      changed = true;
    }
  }
  return changed ? patch : null;
};

/** 클라이언트 검사 — BE 와 같은 규칙. 걸리는 필드별 메시지. */
export const validateProfileInput = (
  current: Record<ProfileField, string>,
  reason: string,
): Partial<Record<ProfileField | 'reason', string>> => {
  const e: Partial<Record<ProfileField | 'reason', string>> = {};
  const name = current.name.trim();
  if (name && name.length > NAME_MAX) e.name = `이름은 ${NAME_MAX}자 이하로 입력하세요.`;
  const email = current.email.trim();
  if (email && (email.length > EMAIL_MAX || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) e.email = '이메일 형식이 아닙니다.';
  const cc = current.mobileCountryCode.trim();
  if (cc && !/^\+?[0-9]{1,4}$/.test(cc)) e.mobileCountryCode = '국가코드는 +82 처럼 숫자 1~4자리입니다.';
  const mobile = current.mobileNumber.trim();
  if (mobile && !/^[0-9\s-]+$/.test(mobile)) e.mobileNumber = '휴대폰 번호는 숫자와 하이픈만 입력하세요.';
  const birth = current.birthDate.trim();
  if (birth && birth >= todayYmd()) e.birthDate = '생년월일은 오늘보다 이전이어야 합니다.';
  const r = reason.trim();
  if (r === '') e.reason = '수정 사유를 입력하세요.';
  else if (r.length > REASON_MAX) e.reason = `수정 사유는 ${REASON_MAX}자 이하로 입력하세요.`;
  return e;
};

const CONFLICT_FIELD: Record<string, ProfileField> = {
  USER_EMAIL_ALREADY_EXISTS: 'email',
  USER_MOBILE_ALREADY_EXISTS: 'mobileNumber',
};

interface ProfileDialogProps {
  user: AdminMemberDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 회원 정보 수정(be-user `PATCH /users/{id}/profile`). 이름·이메일·휴대폰·생년월일 중 바꾼 것만 보내고 사유는 필수다.
 * 유일성은 몰 단위(409 USER_EMAIL_ALREADY_EXISTS / USER_MOBILE_ALREADY_EXISTS), 이메일·휴대폰을 바꾸면 인증 일시가
 * 비워진다. 본인인증 값(CI·본인인증 이름/생년월일)은 바뀌지 않는다. 탈퇴 회원은 수정할 수 없다(409).
 */
export const MemberProfileEditDialog = ({ user, open, onOpenChange }: ProfileDialogProps) => {
  const { t } = useTranslation('common');
  const mutation = useMemberProfileMutation();
  const { reset: resetMutation } = mutation;
  const initial = useMemo<Record<ProfileField, string>>(
    () => ({
      name: editable(user.name),
      email: editable(user.email),
      mobileCountryCode: '',
      mobileNumber: editable(user.mobileNumber),
      birthDate: editable(user.birthDate).slice(0, 10),
    }),
    [user],
  );
  const [form, setForm] = useState(initial);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Partial<Record<ProfileField | 'reason', string>>>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setReason('');
      setErrors({});
      setFormError(null);
      resetMutation();
    }
  }, [open, initial, resetMutation]);

  const set = (k: ProfileField, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const patch = buildProfilePatch(initial, form, reason);
  const emailChanged = form.email.trim() !== '' && form.email.trim() !== initial.email.trim();
  const mobileChanged = form.mobileNumber.trim() !== '' && form.mobileNumber.trim() !== initial.mobileNumber.trim();

  const submit = () => {
    const v = validateProfileInput(form, reason);
    setErrors(v);
    setFormError(null);
    if (Object.keys(v).length > 0 || !patch) return;
    mutation.mutate(
      { userId: user.userId, patch },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => {
          const field = err instanceof MemberCsError && err.code ? CONFLICT_FIELD[err.code] : undefined;
          if (field) {
            setErrors({ [field]: err.message });
            return;
          }
          if (err instanceof MemberCsError && Object.keys(err.fieldErrors).length > 0) {
            setErrors(err.fieldErrors as Partial<Record<ProfileField, string>>);
          }
          setFormError(err.message);
        },
      },
    );
  };

  const field = (k: ProfileField, label: string, input: React.ReactNode) => (
    <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
      {label}
      {input}
      {errors[k] ? (
        <span role="alert" style={{ color: 'var(--status-danger-fg)' }} data-testid={`profile-error-${k}`}>
          {errors[k]}
        </span>
      ) : null}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="member-profile-dialog">
        <DialogHeader>
          <DialogTitle>{t('users.cs.profileTitle', '회원 정보 수정')}</DialogTitle>
          <DialogDescription>
            {t('users.cs.profileDesc', '바꾼 항목만 저장됩니다. 비워 두면 기존 값이 유지됩니다.')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-1" data-sentry-mask>
          {field('name', t('users.fields.name', '이름'), (
            <Input value={form.name} maxLength={NAME_MAX} onChange={(e) => set('name', e.target.value)} aria-label={t('users.fields.name', '이름')} />
          ))}
          {field('email', t('users.fields.email', '이메일'), (
            <Input type="email" value={form.email} maxLength={EMAIL_MAX} onChange={(e) => set('email', e.target.value)} aria-label={t('users.fields.email', '이메일')} />
          ))}
          {emailChanged ? (
            <p className="-mt-1 text-[11px]" style={{ color: 'var(--status-warn-fg)' }} data-testid="profile-email-notice">
              {t('users.cs.emailNotice', '이메일을 변경하면 회원의 로그인 이메일도 변경되고, 이메일 인증은 미인증으로 바뀝니다.')}
            </p>
          ) : null}
          <div className="grid grid-cols-[96px_1fr] gap-2">
            {field('mobileCountryCode', t('users.cs.countryCode', '국가코드'), (
              <Input
                value={form.mobileCountryCode}
                placeholder={t('users.cs.countryCodePlaceholder', '유지')}
                onChange={(e) => set('mobileCountryCode', e.target.value)}
                aria-label={t('users.cs.countryCode', '국가코드')}
              />
            ))}
            {field('mobileNumber', t('users.fields.mobileNumber', '휴대폰'), (
              <Input inputMode="tel" value={form.mobileNumber} onChange={(e) => set('mobileNumber', e.target.value)} aria-label={t('users.fields.mobileNumber', '휴대폰')} />
            ))}
          </div>
          {mobileChanged ? (
            <p className="-mt-1 text-[11px]" style={{ color: 'var(--status-warn-fg)' }} data-testid="profile-mobile-notice">
              {t('users.cs.mobileNotice', '휴대폰 번호를 변경하면 휴대폰 인증은 미인증으로 바뀝니다.')}
            </p>
          ) : null}
          {field('birthDate', t('users.fields.birthDate', '생년월일'), (
            <Input type="date" value={form.birthDate} max={todayYmd()} onChange={(e) => set('birthDate', e.target.value)} aria-label={t('users.fields.birthDate', '생년월일')} />
          ))}
          <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
            {t('users.cs.identityNotice', '본인인증 정보(본인인증 이름·생년월일)는 바뀌지 않습니다. 표시용 정보만 수정됩니다.')}
          </p>
          <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
            <span>
              {t('users.cs.reason', '수정 사유')}
              <span style={{ color: 'var(--signal-danger)' }}> *</span>
            </span>
            <Input
              value={reason}
              maxLength={REASON_MAX}
              placeholder={t('users.cs.reasonPlaceholder', '예: 고객 요청 — 개명')}
              onChange={(e) => setReason(e.target.value)}
              aria-label={t('users.cs.reason', '수정 사유')}
            />
            {errors.reason ? (
              <span role="alert" style={{ color: 'var(--status-danger-fg)' }}>{errors.reason}</span>
            ) : null}
          </label>
          {formError ? (
            <p role="alert" className="rounded px-2 py-1.5 text-[12px]" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
              {formError}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            {t('users.cs.cancel', '취소')}
          </Button>
          <Button type="button" size="sm" onClick={submit} disabled={mutation.isPending || !patch} data-testid="profile-save">
            {patch ? t('users.cs.save', '저장') : t('users.cs.noChange', '변경된 항목 없음')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * 휴대폰으로 알림톡을 받을 수 있어 보이는지 — 비었거나 010/011/016~019 로 시작하지 않으면(이관 무효값 000… 등) 아니다.
 * 마스킹된 값("010-****-5678")도 앞자리는 남아 있어 판정할 수 있다.
 */
export const phoneLooksReachable = (mobile: string | null | undefined): boolean => {
  const digits = (mobile ?? '').replace(/[^0-9]/g, '');
  return /^01[016789]/.test(digits);
};

/**
 * 기본 채널 — 한국몰은 휴대폰(알림톡), 해외몰은 이메일. 휴대폰이 비었거나 무효로 보이면 이메일.
 * mallCode 가 없으면(구버전 응답) 한국몰로 본다.
 */
export const defaultResetChannel = (user: Pick<AdminMemberDetail, 'mallCode' | 'mobileNumber'>): PasswordResetChannel => {
  const korean = !user.mallCode || user.mallCode === 'ko-KR';
  if (!korean) return 'EMAIL';
  return phoneLooksReachable(user.mobileNumber) ? 'PHONE' : 'EMAIL';
};

interface ResetDialogProps {
  user: AdminMemberDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 비밀번호 초기화 — 재설정 인증번호 대행 발송(be-user `POST /users/{id}/password/reset-code`).
 * 관리자는 비밀번호를 보거나 정하지 않는다. 고객이 쇼핑몰 [비밀번호 찾기]에서 받은 인증번호로 새 비밀번호를 정한다.
 * 활성·휴면 회원만 된다(그 외 409). 휴면 회원은 고객이 새 비밀번호를 정하면 휴면도 해제된다(user-service #25).
 * 등록된 연락처가 없으면 400 → 회원 정보 수정이 먼저다.
 */
export const PasswordResetCodeDialog = ({ user, open, onOpenChange }: ResetDialogProps) => {
  const { t } = useTranslation('common');
  const mutation = usePasswordResetCodeMutation();
  const { reset: resetMutation } = mutation;
  const initialChannel = defaultResetChannel(user);
  const phoneUnreachable = !phoneLooksReachable(user.mobileNumber);
  const [channel, setChannel] = useState<PasswordResetChannel>(initialChannel);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<PasswordResetCodeResult | null>(null);

  useEffect(() => {
    if (open) {
      setChannel(initialChannel);
      setReason('');
      setResult(null);
      resetMutation();
    }
  }, [open, initialChannel, resetMutation]);

  const err = mutation.error;
  const hint =
    err?.status === 400
      ? t('users.cs.resetHintContact', '회원 정보를 먼저 수정하거나 다른 채널을 선택해 주세요.')
      : err?.status === 409
        ? t('users.cs.resetHintInactive', '활성·휴면 회원에게만 보낼 수 있습니다.')
        : null;
  const reasonValid = reason.trim() !== '' && reason.trim().length <= REASON_MAX;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]" data-testid="password-reset-dialog">
        <DialogHeader>
          <DialogTitle>{t('users.cs.resetTitle', '비밀번호 초기화')}</DialogTitle>
          <DialogDescription>
            {t(
              'users.cs.resetDesc',
              '회원의 휴대폰(또는 이메일)로 비밀번호 재설정 인증번호를 보냅니다. 고객이 쇼핑몰 비밀번호 찾기에서 인증번호를 입력해 새 비밀번호를 설정해야 합니다.',
            )}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <p
            role="status"
            className="rounded px-2 py-2 text-sm"
            style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
            data-testid="password-reset-sent"
          >
            {t('users.cs.resetSent', { to: result.sentTo, defaultValue: '{{to}} 로 발송했습니다.' })}
          </p>
        ) : (
          <div className="flex flex-col gap-3 py-1">
            <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
              {t('users.cs.resetChannel', '보낼 곳')}
              <Select value={channel} onChange={(e) => setChannel(e.target.value as PasswordResetChannel)} aria-label={t('users.cs.resetChannel', '보낼 곳')}>
                <option value="PHONE">{t('users.cs.channelPhone', '휴대폰(알림톡)')}</option>
                <option value="EMAIL">{t('users.cs.channelEmail', '이메일')}</option>
              </Select>
            </label>
            {phoneUnreachable ? (
              <p className="-mt-1 text-[11px]" style={{ color: 'var(--status-warn-fg)' }} data-testid="password-reset-phone-warning">
                {t('users.cs.phoneUnreachable', '등록된 휴대폰으로는 받을 수 없을 수 있습니다.')}
              </p>
            ) : null}
            {user.status === 'DORMANT' ? (
              <p className="text-[11px]" style={{ color: 'var(--status-warn-fg)' }} data-testid="password-reset-dormant-note">
                {t('users.cs.resetDormantNote', '휴면 회원입니다. 고객이 인증번호로 새 비밀번호를 정하면 휴면도 함께 해제됩니다.')}
              </p>
            ) : null}
            {channel === 'EMAIL' ? (
              <p className="text-[11px]" style={{ color: 'var(--content-muted)' }} data-testid="password-reset-email-id-note">
                {t('users.cs.resetEmailIdNote', '이메일이 다른 계정과 겹치는 회원은 쇼핑몰 비밀번호 찾기에서 아이디를 입력해야 합니다.')}
              </p>
            ) : null}
            {/* 이관 회원 CS 기준 — 고도몰 비밀번호를 옮기지 않아 전원 재설정이 필요하다. */}
            <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
              {t(
                'users.cs.migratedNotice',
                '고도몰 이관 회원은 비밀번호가 없어 첫 로그인 전에 재설정이 필요합니다. 재설정을 마치면 바로 로그인됩니다.',
              )}
            </p>
            <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
              <span>
                {t('users.cs.resetReason', '사유')}
                <span style={{ color: 'var(--signal-danger)' }}> *</span>
              </span>
              <Input
                value={reason}
                maxLength={REASON_MAX}
                placeholder={t('users.cs.resetReasonPlaceholder', '예: 고객 전화 요청 — 비밀번호 분실')}
                onChange={(e) => setReason(e.target.value)}
                aria-label={t('users.cs.resetReason', '사유')}
              />
            </label>
            {err ? (
              <div role="alert" className="rounded px-2 py-1.5 text-[12px]" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
                <p>{err.message}</p>
                {hint ? <p className="mt-0.5">{hint}</p> : null}
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button type="button" size="sm" onClick={() => onOpenChange(false)}>
              {t('users.cs.close', '닫기')}
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                {t('users.cs.cancel', '취소')}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={mutation.isPending || !reasonValid}
                onClick={() =>
                  mutation.mutate(
                    { userId: user.userId, channel, reason: reason.trim() },
                    { onSuccess: setResult },
                  )
                }
                data-testid="password-reset-send"
              >
                {t('users.cs.resetSend', '인증번호 보내기')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
